import { Dtos } from "@inrev/common";
import { useAdminFetchAdminUsers, useAdminReassignUnderwriter } from "@portal/api/admin-user/admin";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import { AdminCreateAgencyForm } from "@portal/components/features/create-agency";
import { IslandLayout } from "@portal/components/layout/IslandLayout";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button } from "@portal/components/ui/Button";
import { Card } from "@portal/components/ui/Card";
import { useState } from "react";
import { HiOutlinePlus } from "react-icons/hi2";
import { NavLink } from "react-router-dom";
import { AdminUserSelect } from "../shared/AdminUserSelect";
import { AdminUserMenu } from "./AdminUserMenu";

export const ManageAgenciesView = () => {
	const [showAddAgencyModal, setShowAddAgencyModal] = useState(false);
	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();
	const { adminUsers, adminUsersLoading } = useAdminFetchAdminUsers();
	const { reassignUnderwriter, reassignUnderwriterLoading } = useAdminReassignUnderwriter();

	const handleModalClose = () => {
		setShowAddAgencyModal(false);
	};

	return (
		<>
			<IslandLayout userMenu={<AdminUserMenu />}>
				<div className="flex flex-col gap-[30px] w-[1000px] max-w-[1000px] h-fit flex-flex-col mt-[25px] mx-[40px] pb-[75px]">
					<div className="flex w-full justify-between gap-[25px]">
						<span
							data-testid="manage-agencies-title"
							className="text-[26px] text-gray-800 font-semibold"
						>
							Manage Agencies
						</span>
						<Button
							testId="add-agency-button"
							onClick={() => setShowAddAgencyModal(true)}
							color="primary"
							thinFont
							filled
						>
							<HiOutlinePlus className="mr-1" />
							Add Agency
						</Button>
					</div>
					<div className="w-full flex flex-col">
						<Card className="flex items-center bg-surface-neutral w-full gap-[12px] px-[12px] py-[5px] border-gray-400 text-[14px] font-medium text-secondary">
							<div data-testid="agency-header" className="flex-[3] truncate">
								Agency
							</div>
							<div data-testid="underwriter-header" className="flex-[2] truncate">
								Underwriter
							</div>
						</Card>
						{!!agencyPreviews && !agencyPreviewsLoading && !!adminUsers && !adminUsersLoading && (
							<div
								data-testid="agency-list"
								className="w-full relative h-fit flex flex-col text-gray-800"
							>
								{agencyPreviews.length > 0 ? (
									agencyPreviews.map((agency) => {
										return (
											<div
												key={agency.id}
												data-testid="agency-row"
												className="flex items-center gap-[12px] w-full px-[12px] py-[18px] border-b border-b-gray-200/80"
											>
												<NavLink
													to={`/agencies/${agency.id}`}
													className="flex-[3] truncate text-link hover:text-link-hover"
												>
													{agency.displayName}
												</NavLink>
												<div className="flex-[2] h-[50px] truncate">
													<AdminUserSelect
														entity={{ entityType: "agency", entityId: agency.id }}
														currentAssignee={agency.underwriterUserId!}
														adminUsers={adminUsers}
														onChange={(value) => {
															const dto: Dtos.Admin.UnderwriterAssignment.Assign.Request = {
																entityType: "agency",
																entityId: agency.id,
																underwriterUserId: value,
															};
															reassignUnderwriter(dto);
														}}
														disabled={reassignUnderwriterLoading}
														className={"absolute"}
													/>
												</div>
											</div>
										);
									})
								) : (
									<div
										data-testid="empty-agency-label"
										className="w-full px-[12px] py-[18px] pt-8 text-center text-tertiary italic"
									>
										No agencies found
									</div>
								)}
							</div>
						)}
						{(agencyPreviewsLoading || !!agencyPreviews || adminUsersLoading || !!adminUsers) && (
							<div className="w-full px-[12px] py-[18px]">
								<div className="w-full h-[30px] bg-gray-100 animate-pulse rounded-md"></div>
							</div>
						)}
					</div>
				</div>
			</IslandLayout>
			{showAddAgencyModal && (
				<Modal onClickOutside={handleModalClose}>
					<ModalItemWithHeader
						header={
							<div data-testid="add-agency-modal">
								<div className="text-base font-medium">Add Agency</div>
							</div>
						}
						onClose={handleModalClose}
					>
						<AdminCreateAgencyForm onClose={handleModalClose} />
					</ModalItemWithHeader>
				</Modal>
			)}
		</>
	);
};
